/* eslint-disable @typescript-eslint/no-explicit-any */
import { Field, Form, Formik, FormikValues, useFormik } from "formik";
import Container from "./Container";
import FormField from "./FormField";
import Text from "./Text";
import { Button } from "primereact/button";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { Divider } from "primereact/divider";
import { useEffect, useRef, useState } from "react";
import Checkbox from "./Checkbox";
import ForgotPasswordSidebar from "./ForgotPasswordSidebar";
import { useForgotPassword } from "../contexts/ForgotPasswordContext";
import { Message } from "primereact/message";
import { useAuthContext } from "../contexts/AuthContext";
import { OAUTH_FEDERATION_SESSION_KEY, STORAGE_KEYS } from "../services/logoutSession";
import { Dialog } from "primereact/dialog";
import { InputOtp } from "primereact/inputotp";
import { Toast } from "primereact/toast";

const API_URL = import.meta.env.VITE_API_URL;

interface ILoginResponse {
    message: string;
    data: {
        id_token: string;
        access_token: string;
        expires_in: string;
        refresh_token: string;
    };
}

interface ILoginFormProps {
    onSignUp: () => void;
}

export default function LoginForm({ onSignUp }: ILoginFormProps) {
    const forgotPasswordContext = useForgotPassword();
    const authContext = useAuthContext();
    const [shouldConfirmUser, setShouldConfirmUser] = useState(false);
    const [message, setMessage] = useState<string | null>("");

    const toast = useRef<Toast | null>(null);
    
    const [formInitialValues, setFormInitialValues] = useState({
        email: "",
        password: "",
        rememberMe: false
    });

    useEffect(() => {
        const rememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME);

        if (rememberMe == "true") {
            setFormInitialValues({
                email: localStorage.getItem(STORAGE_KEYS.EMAIL) ?? "",
                password: localStorage.getItem(STORAGE_KEYS.PASSWORD) ?? "",
                rememberMe: true
            });
        }
    }, []);

    const loginMutation = useMutation({
        mutationFn: async ({email, password} : { email: string; password: string; }) => {
            try {
                const loginResponse = await axios.post<ILoginResponse>(
                    `${API_URL}/user/login`, 
                    { 
                        username: email, 
                        password 
                    },
                    {
                        headers: {
                            "Content-Type": "text/plain"
                        }
                    });
                
                localStorage.setItem(STORAGE_KEYS.TOKEN, loginResponse.data.data.id_token);
                localStorage.setItem(STORAGE_KEYS.REFRESH, loginResponse.data.data.refresh_token);
                localStorage.setItem(STORAGE_KEYS.FEDERATION, "google");
                setFormInitialValues({
                    email: email,
                    password: password,
                    rememberMe: false
                });
                authContext.login(loginResponse.data.data.id_token);
    
                return loginResponse;
            } catch (err) {
                if ((err as any).response?.data?.message === "User is not confirmed") {
                    setFormInitialValues({
                        email: email,
                        password: password,
                        rememberMe: false
                    });
                    setShouldConfirmUser(true);
                }
                throw new Error((err as any).response?.data?.message ?? (err as AxiosError).message);
            }
        }
    });

    const confirmUserMutation = useMutation({
        mutationFn: async ({email, code} : { email: string; code: string; }) => {
            return axios.post(`${API_URL}/user/signin`, { username: email, code }, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
        },
        onSuccess: () => {
            toast.current?.show({
                severity: "success",
                summary: "Usuário confirmado com sucesso",
            });

            setMessage("Faça o login para continuar");
            
            setShouldConfirmUser(false);
        },
        onError: (error) => {
            toast.current?.show({
                severity: "error",
                summary: error.message,
            });
        }
    });

    const signInGoogle = () => {
        sessionStorage.setItem(OAUTH_FEDERATION_SESSION_KEY, "google");
        const clientId = import.meta.env.VITE_COGNITO_CLIENTID;
        const cognitoDomain = import.meta.env.VITE_COGNITO_USERPOOLURL;
        const redirectUri = import.meta.env.VITE_COGNITO_REDIRECTURI;

        const authorizationUrl = `${cognitoDomain}/oauth2/authorize?identity_provider=Google&redirect_uri=${redirectUri}&response_type=token&client_id=${clientId}`;
        window.location.href = authorizationUrl;
    }

    const signInApple = async () => {
        sessionStorage.setItem(OAUTH_FEDERATION_SESSION_KEY, "apple");
        const clientId = import.meta.env.VITE_COGNITO_CLIENTID_APPLE;
        const cognitoDomain = import.meta.env.VITE_COGNITO_USERPOOLURL;
        const redirectUri = import.meta.env.VITE_COGNITO_REDIRECTURI;

        const authorizationUrl = `${cognitoDomain}/oauth2/authorize?identity_provider=Apple&redirect_uri=${redirectUri}&response_type=token&client_id=${clientId}`;
        window.location.href = authorizationUrl;
    }

    const confirmUserFormik = useFormik({
        initialValues: {
            code: ""
        },
        validationSchema: Yup.object().shape({
            code: Yup.string().required("Campo código de verificação obrigatório"),
        }),
        onSubmit: async (values) => {
            await confirmUserMutation.mutateAsync({ email: formInitialValues.email, code: values.code });
        }
    });

    return (
        <>
            <Toast ref={toast} />

            <Dialog
                visible={shouldConfirmUser}
                onHide={() => setShouldConfirmUser(false)}
                header="Usuário não confirmado"
                footer={
                    <Button loading={confirmUserMutation.isPending} disabled={confirmUserMutation.isPending} label="Confirmar" onClick={() => confirmUserFormik.handleSubmit()} />
                }
            >
                <Text>O usuário não foi confirmado. Por favor, verifique seu email para confirmar sua conta.</Text>
                <FormField
                    label="Código de verificação"
                    id="code"
                    type="text"
                    hasError={!!confirmUserFormik.errors.code && !!confirmUserFormik.touched.code}
                    error={confirmUserFormik.errors.code as string}
                >
                    <InputOtp
                        length={6}
                        onChange={(e) => confirmUserFormik.setFieldValue("code", e.value)}
                    />
                </FormField>
            </Dialog>

            <Container flexColumn fullWidth maxWidth={500} paddingHorizontal="6" paddingVertical="3">
                <Formik
                    initialValues={formInitialValues}
                    enableReinitialize
                    validationSchema={Yup.object().shape({
                        email: Yup.string()
                            .email("Valor informado não é um email")
                            .required("Campo email obrigatório"),
                        password: Yup.string()
                            .required("Campo senha obrigatório"),
                        rememberMe: Yup.bool()
                    })}
                    onSubmit={async (values: FormikValues) => {
                        await loginMutation.mutateAsync({ email: values.email, password: values.password });
                    }}
                >
                    {
                        ({ errors, setFieldValue, values }) => (
                            <Form>
                                <FormField 
                                    label="Endereço de Email" 
                                    id="email" 
                                    type="email"
                                />

                                <FormField 
                                    label="Senha" 
                                    id="password" 
                                    type="password"
                                    secureText
                                    action={
                                        <Text 
                                            color="primary" 
                                            size="xs" 
                                            onClick={() => forgotPasswordContext?.open(values.email)}
                                        >
                                            Esqueceu a senha?
                                        </Text>
                                    }
                                />

                                <Field name="rememberMe">
                                    {
                                        () => (
                                            <Checkbox 
                                                id="rememberMe"
                                                label="Lembrar me"
                                                name="rememberMe"
                                                checked={values.rememberMe}
                                                disabled={!!errors.email || !!errors.password}
                                                onChange={(rememberMe) => {
                                                    setFieldValue("rememberMe", rememberMe);
                                                    localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, rememberMe.toString());

                                                    if (rememberMe) {
                                                        localStorage.setItem(STORAGE_KEYS.EMAIL, values.email);
                                                        localStorage.setItem(STORAGE_KEYS.PASSWORD, values.password);
                                                    } else {
                                                        localStorage.removeItem(STORAGE_KEYS.EMAIL);
                                                        localStorage.removeItem(STORAGE_KEYS.PASSWORD);
                                                    }
                                                }} 
                                            />
                                        )
                                    }
                                </Field>

                                <Button 
                                    label={loginMutation.isPending ? "Entrando..." : "Entrar"} 
                                    type="submit" 
                                    loading={loginMutation.isPending}
                                    className="bg-primary w-full" 
                                />

                                {
                                    loginMutation?.error && <Message className="mt-2 w-full" severity="error" text={loginMutation.error.message} />
                                }

                                {
                                    message && <Message className="mt-2 w-full" severity="success" text={message} />
                                }

                                <Divider align="center" className="my-5">
                                    <span className="p-tag bg-gray-100 text-300">ou entre com</span>
                                </Divider>

                                <Button 
                                    label="Continuar com o Google"
                                    type="button"
                                    icon="pi pi-google"
                                    onClick={() => signInGoogle()}
                                    className="bg-gray-200 w-full border-gray-200 mt-2 text-600 font-light" 
                                />

                                <Button 
                                    label="Continuar com a Apple"
                                    type="button"
                                    icon="pi pi-apple"
                                    onClick={() => signInApple()}
                                    className="bg-gray-200 w-full border-gray-200 mt-2 text-600 font-light" 
                                />

                                <Text center size="xs" className="mt-2" color="primary" onClick={() => onSignUp()}>
                                    Crie uma conta
                                </Text>
                            </Form>
                        )
                    }
                </Formik>

                <ForgotPasswordSidebar />
            </Container>
        </>
    );
}
